import { useEffect, useRef } from 'preact/hooks';
import { lazy, Suspense } from 'preact/compat';
import { StoreProvider, useStore } from './store';
import { TopHud } from './components/TopHud';
import { AwayBanner } from './components/AwayBanner';
import { TabBar } from './components/TabBar';
import { TipCard } from './components/TipCard';
import { SettingsDrawer } from './components/SettingsDrawer';
import { FieldHud } from './components/FieldHud';
import { ExpeditionAdvance } from './components/ExpeditionAdvance';
import { StartFlow } from './components/StartFlow';
import { DecisionHost } from './components/DecisionHost';
import { PhotoPanel } from './components/PhotoPanel';
import { Sheet } from './components/Sheet';
import { ReserveScreen } from './screens/ReserveScreen';
import { MapScreen } from './screens/MapScreen';
import { HerdScreen } from './screens/HerdScreen';
import { ParkScreen } from './screens/ParkScreen';
import { OfficeScreen } from './screens/OfficeScreen';
import { attachViewportGestures } from './input/touch';

/**
 * The 3D viewport host. Lane 3 mounts Lane 2's ReserveScene into
 * #world-canvas-host (src/ui/world.ts) and pumps frames from the bridge clock.
 * Gestures stay on the host div only - attaching to the whole viewport lets
 * pointer capture eat UI clicks (fixed in v1). The standin gradient remains
 * as the fallback if WebGL or the scene import fails.
 */
function Viewport() {
  const hostRef = useRef<HTMLDivElement>(null);
  const worldRef = useRef<{ setFieldView(view: unknown): void } | null>(null);
  const { bridge, mode } = useStore();
  const modeRef = useRef(mode);
  const pushFieldTo = (world: { setFieldView(view: unknown): void }) => {
    const st = bridge.getState() as { fieldWorld?: unknown };
    world.setFieldView(modeRef.current === 'field' ? st.fieldWorld ?? null : null);
  };
  useEffect(() => {
    modeRef.current = mode;
    if (worldRef.current) pushFieldTo(worldRef.current);
  }, [mode]);
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const detachGestures = attachViewportGestures(host, (intent) => {
      if(modeRef.current==='field'&&intent.type==='pinch'){const st=bridge.getState();bridge.send('field.zoom',{zoomBp:Math.round((st.fieldCapture?.zoomBp??10000)*intent.scale)});return;}
      window.dispatchEvent(new CustomEvent('ea:camera', { detail: intent }));
    });
    let disposed = false;
    let raf = 0;
    let unsub: (() => void) | null = null;
    let last = performance.now();
    void import('./world').then(({ mountWorld }) => mountWorld(host)).then((world) => {
      if (disposed) { world.dispose(); return; }
      host.querySelector('.viewport-standin')?.remove();
      // Ecology drives pasture look: ui.overlay forageKg + habitat droughtActive (render reads, never writes).
      const pushPasture = (st: { pasture: { forageKg: Array<number | null>; droughtActive: boolean } | null }) => { world.setPasture(st.pasture); };
      pushPasture(bridge.getState());
      worldRef.current = world;
      pushFieldTo(world);
      unsub = bridge.subscribe((st) => { pushPasture(st); pushFieldTo(world); });
      const pump = (now: number) => {
        if (disposed) return;
        const dt = Math.min(now - last, 100);
        last = now;
        world.render(bridge.tickFloat(), dt);
        raf = requestAnimationFrame(pump);
      };
      raf = requestAnimationFrame(pump);
    }).catch((err: unknown) => {
      console.warn('world mount failed, standin stays', err);
    });
    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      unsub?.();
      detachGestures();
    };
  }, [bridge]);  return (
    <div class="viewport">
      <div id="world-canvas-host" class="viewport-canvas-host" ref={hostRef} />
      <ViewportScreens />
    </div>
  );
}

function ViewportScreens() {
  const { tab, panel, closePanel } = useStore();
  return (
    <div class="viewport-overlay">
      <ExpeditionAdvance />
      {tab === 'reserve' && <FieldHud />}
      {tab === 'reserve' && <ReserveScreen />}
      {tab === 'map' && <MapScreen />}
      {tab === 'herd' && <HerdScreen />}
      {tab === 'park' && <ParkScreen />}
      {tab === 'office' && <OfficeScreen />}
      {panel?.kind === 'photo' && (
        <Sheet label="Photo mode" onClose={closePanel}>
          <PhotoPanel />
        </Sheet>
      )}
    </div>
  );
}

function Shell() {
  const { path, sheetsOpen } = useStore();
  if (!path) return <div class="shell"><StartFlow /></div>;
  return (
    <div class="shell">
      <TopHud />
      <AwayBanner />
      <Viewport />
      <DecisionHost />
      {sheetsOpen === 0 ? <TipCard /> : null}
      <TabBar />
      <SettingsDrawer />
      {DebugConsoleLazy ? <Suspense fallback={null}><DebugConsoleLazy /></Suspense> : null}
    </div>
  );
}

// P014: the debug console is dev-only. The lazy() branch is dead code in production
// builds (import.meta.env.DEV === false), so Rollup drops the dynamic import and no
// console chunk or string reaches the shipped bundle. ?debug=1/backtick in production
// therefore cannot mount or send anything.
const DebugConsoleLazy = import.meta.env.DEV
  ? lazy(() => import('./components/DebugConsole').then((m) => ({ default: m.DebugConsole })))
  : null;

export function App() {
  return (
    <StoreProvider>
      <Shell />
    </StoreProvider>
  );
}
